import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../lib/auth";
import { supabase } from "../../lib/supabase";
import { useTempoReal } from "../../lib/tempoReal";
import { mensagemDeErro, novaChave, rpc } from "../../lib/rpc";
import { produtoPorCodigo } from "../../lib/leitura";
import { sinalConcluido, sinalErro, sinalOk } from "../../lib/sinais";
import { fmtDataHora, fmtNum, nomeTipoLocal } from "../../lib/formato";
import { LeituraCodigo } from "../../componentes/LeituraCodigo";
import { AlertaErro, Confirmar } from "../../componentes/Alerta";
import { Botao, Selo, Vazio } from "../../componentes/ui";

interface TarefaContagem {
  id: string; deposito_id: string; local_id: string; local: string; local_tipo: string; rua: string | null;
  status: string; contagem: number; motivo: string; criado_em: string; bloqueia_separacao: boolean;
  inventario_id: string | null; inventario_numero: number | null; inventario_tipo: string | null;
  produto_id: string | null; sku: string | null; produto: string | null;
}
interface Contado { produto_id: string; codigo: string; nome: string; sku: string | null; quantidade: number }

const resultadoTexto: Record<string, string> = {
  ok: "Contagem bateu com o sistema. Posição fechada.",
  recontar: "Não bateu. Outra pessoa vai recontar esta posição.",
  revisao: "Não bateu. O líder vai revisar a contagem.",
  ajustado: "Recontagem confirmou a diferença. Saldo ajustado.",
};

/** Contagens pedidas pelo líder (e posições com falta na coleta). Contagem cega. */
export default function Contagem() {
  const { perfil } = useAuth();
  const [tarefa, setTarefa] = useState<TarefaContagem | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const tarefas = useQuery({
    queryKey: ["contagens", perfil?.deposito_padrao_id],
    queryFn: async () => {
      let q = supabase.from("v_inventario_tarefas").select("*").order("criado_em").limit(200);
      if (perfil?.deposito_padrao_id) q = q.eq("deposito_id", perfil.deposito_padrao_id);
      const { data, error } = await q;
      if (error) throw error;
      return data as TarefaContagem[];
    },
    refetchInterval: 30_000,
  });
  useTempoReal(["tarefas_inventario"], [["contagens"]]);

  if (tarefa) {
    return <Contar tarefa={tarefa} onFim={(msg) => { setTarefa(null); setAviso(msg); void tarefas.refetch(); }} />;
  }

  const lista = tarefas.data ?? [];
  return (
    <div className="flex flex-col gap-4">
      <h1 className="font-sinal text-3xl font-bold">Contagens pedidas</h1>
      {aviso && <p className="rounded-md bg-info-claro px-3 py-3 text-lg font-semibold text-info" role="status">{aviso}</p>}
      {lista.length ? (
        <ul className="flex flex-col gap-2">
          {lista.map((t) => (
            <li key={t.id}>
              <button type="button" onClick={() => setTarefa(t)}
                className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg border-2 border-aco bg-chapa px-4 py-3 text-left hover:border-tinta">
                <span>
                  <span className="block font-sinal text-3xl font-bold">{t.local}</span>
                  <span className="text-aco-escuro">{nomeTipoLocal[t.local_tipo] ?? t.local_tipo} · {t.motivo}</span>
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  {t.inventario_numero ? <Selo tom="info">Inventário {t.inventario_numero}</Selo> : <Selo tom="alerta">Falta na coleta</Selo>}
                  {t.contagem > 1 && <Selo tom="faixa">{t.contagem}ª contagem</Selo>}
                  {t.sku && <Selo>Só {t.sku}</Selo>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : <Vazio>{tarefas.isLoading ? "Carregando..." : "Nenhuma contagem pendente."}</Vazio>}
      <Link to="/estoque/inventario" className="text-center font-semibold underline">Contar outra posição por conta própria</Link>
    </div>
  );
}

function Contar({ tarefa, onFim }: { tarefa: TarefaContagem; onFim: (mensagem: string) => void }) {
  const [naPosicao, setNaPosicao] = useState(false);
  const [contados, setContados] = useState<Contado[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [lampejo, setLampejo] = useState(0);
  const [tremor, setTremor] = useState(0);

  function falhou(m: string) {
    sinalErro();
    setTremor((n) => n + 1);
    setErro(m);
  }

  async function leitura(codigo: string) {
    if (!naPosicao) {
      if (codigo.trim().toUpperCase() !== tarefa.local) return falhou(`Posição errada. Vá até ${tarefa.local}`);
      sinalOk();
      setLampejo((n) => n + 1);
      setNaPosicao(true);
      return;
    }
    const p = await produtoPorCodigo(codigo);
    if (!p) return falhou("Código não cadastrado");
    if (tarefa.produto_id && p.id !== tarefa.produto_id) return falhou(`Este inventário conta só o ${tarefa.sku ?? tarefa.produto}`);
    setContados((atual) => atual.some((c) => c.produto_id === p.id)
      ? atual.map((c) => (c.produto_id === p.id ? { ...c, quantidade: c.quantidade + 1 } : c))
      : [...atual, { produto_id: p.id, codigo, nome: p.nome, sku: p.codigo, quantidade: 1 }]);
    sinalOk();
    setLampejo((n) => n + 1);
  }

  function ajustar(id: string, valor: number) {
    setContados((atual) => atual.map((c) => (c.produto_id === id ? { ...c, quantidade: Math.max(0, valor) } : c)));
  }

  async function concluir() {
    setOcupado(true);
    try {
      const r = await rpc<{ ok: boolean; resultado?: string; ajustes?: Array<{ sku: string; antes: number; depois: number }>; mensagem?: string }>(
        "wms_inventario_contar",
        { p_tarefa_id: tarefa.id, p_contagem: contados.map((c) => ({ codigo: c.codigo, quantidade: c.quantidade })), p_idem: novaChave() },
      );
      setConfirmar(false);
      if (!r.ok) return falhou(r.mensagem ?? "Contagem recusada");
      sinalConcluido();
      if (r.resultado) onFim(`${tarefa.local}: ${resultadoTexto[r.resultado] ?? r.resultado}`);
      else onFim(`${tarefa.local}: ${r.ajustes?.length ? `${r.ajustes.length} produto(s) ajustado(s)` : "sem diferença"}`);
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />
      <section className="rounded-lg border-2 border-tinta bg-chapa">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-aco px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-aco-escuro">{naPosicao ? "Contando" : "Vá até a posição"}</p>
            <p className="font-sinal text-6xl font-bold leading-none">{tarefa.local}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {tarefa.inventario_numero && <Selo tom="info">Inventário {tarefa.inventario_numero}</Selo>}
            {tarefa.contagem > 1 && <Selo tom="faixa">{tarefa.contagem}ª contagem</Selo>}
          </div>
        </div>
        {tarefa.sku && <p className="border-b border-aco bg-faixa-clara px-4 py-2 font-semibold">Conte só {tarefa.sku} {tarefa.produto}</p>}
        {naPosicao && (contados.length ? (
          <ul className="divide-y divide-aco">
            {contados.map((c) => (
              <li key={c.produto_id} className="flex items-center justify-between gap-2 px-4 py-2">
                <div className="min-w-0"><p className="font-semibold">{c.nome}</p><p className="text-sm text-aco-escuro">{c.sku}</p></div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => ajustar(c.produto_id, c.quantidade - 1)} aria-label="Menos um"
                    className="h-12 w-12 rounded-md border-2 border-tinta text-2xl font-bold">-</button>
                  <input type="number" inputMode="numeric" value={c.quantidade} aria-label={`Quantidade de ${c.nome}`}
                    onChange={(e) => ajustar(c.produto_id, Number(e.target.value))}
                    className="h-12 w-20 rounded-md border-2 border-tinta text-center font-sinal text-2xl font-bold" />
                  <button type="button" onClick={() => ajustar(c.produto_id, c.quantidade + 1)} aria-label="Mais um"
                    className="h-12 w-12 rounded-md border-2 border-tinta text-2xl font-bold">+</button>
                </div>
              </li>
            ))}
          </ul>
        ) : <div className="p-4"><Vazio>Bipe cada unidade da posição. Para muitas iguais, bipe uma e digite a quantidade. Vazia: conclua sem bipar.</Vazio></div>)}
        <p className="border-t border-aco px-4 py-2 text-sm text-aco-escuro">{tarefa.motivo} · pedida {fmtDataHora(tarefa.criado_em)}</p>
      </section>

      <LeituraCodigo rotulo={naPosicao ? "Bipe os produtos" : "Bipe a posição"} onLeitura={leitura} desabilitado={ocupado} lampejo={lampejo} erro={tremor} />

      {naPosicao && <Botao variante="confirmar" grande onClick={() => setConfirmar(true)}>Concluir contagem ({fmtNum(contados.reduce((s, c) => s + c.quantidade, 0))} un)</Botao>}
      <Botao variante="secundario" onClick={() => onFim("Contagem deixada para depois")}>Voltar para a lista</Botao>

      <Confirmar aberto={confirmar} titulo={`Gravar a contagem de ${tarefa.local}?`} rotuloConfirmar="Gravar contagem" ocupado={ocupado}
        onConfirmar={() => void concluir()} onCancelar={() => setConfirmar(false)}>
        O que não foi bipado conta como zero {tarefa.sku ? `de ${tarefa.sku}` : "nesta posição"}.
      </Confirmar>
    </div>
  );
}
